import React, { RefObject, useContext } from "react";
import {
  DrugCostInfo,
  DrugInfo,
  Plan,
  PlanDetailsPageSection,
  PrescriptionDrug,
} from "../../../@types";
import { PlanDetailSection } from "../components";
import { useTranslate } from "../../../helpers";
import { PharmaciesTable } from "./PharmaciesTable";
import { CostsByCoveragePhaseTable } from "./CostsByCoveragePhaseTable";
import { useGetPharmacies } from "./drugCoverageHelpers";
import EstimatedMonthlyCosts from "./EstimatedMonthlyCosts";
import { EstimatedTotalDrugAndPremiumCostTable } from "./EstimatedTotalDrugAndPremiumCostTable";
import { YearlyDrugCostsByPharmacyTable } from "./YearlyDrugCostsByPharmacyTable";
import { PartBDrugsTable } from "./PartBDrugsTable";
import { MyDrugListTable } from "./MyDrugListTable";
import { OtherDrugInformationTable } from "./OtherDrugInformationTable";
import { ToggleMoreBenefits } from "../components/ToggleMoreBenefits";
import { CostsByDrugTierTable } from "./CostsByDrugTierTable";
import { linkToPrescriptionCostHelp } from "../../../helpers/CONSTANTS";
import { AppStore } from "../../../app/store";

export const DrugCoverageSection: React.FunctionComponent<{
  plan: Plan;
  drugCostInfo: DrugCostInfo;
  drugInfo: DrugInfo[];
  prescriptions: PrescriptionDrug[];
  sectionRef: RefObject<HTMLDivElement>;
}> = ({ plan, drugCostInfo, sectionRef, prescriptions, drugInfo }) => {
  const t = useTranslate();
  const {
    state: { language },
  } = useContext(AppStore.AppContext);
  const pharmacies = useGetPharmacies(drugCostInfo);
  const hasPharmacies = pharmacies.length > 0;
  const hasPrescriptions = prescriptions.length > 0;

  return (
    <PlanDetailSection
      className="mct-print-break-before--page"
      id={PlanDetailsPageSection.DRUG_COVERAGE}
      title={t("plan_details.drug_coverage")}
      sectionRef={sectionRef}
      headingAddOn={
        <a
          className="ds-u-color--secondary"
          target="_blank"
          href={linkToPrescriptionCostHelp(language)}
          rel="noopener noreferrer"
        >
          {t("plan_details.reduce_drug_cost_link")}
        </a>
      }
    >
      {/* Tables go here, take one of or both plan and drugCostInfo data */}

      {/* Pharmacies table - Must have prescriptions and pharmacies */}
      {hasPharmacies && hasPrescriptions && (
        <PharmaciesTable drugCostInfo={drugCostInfo} plan={plan} />
      )}
      {/* Yearly Drug Cost By Pharmacy - (New table) Must have prescriptions and pharmacies */}
      {hasPharmacies && hasPrescriptions && (
        <YearlyDrugCostsByPharmacyTable
          plan={plan}
          drugCostInfo={drugCostInfo}
        />
      )}
      {/* Estimated Total Drug Cost + Premium Cost - Must have prescriptions and pharmacies (I think) */}
      {hasPharmacies && hasPrescriptions && (
        <EstimatedTotalDrugAndPremiumCostTable
          plan={plan}
          drugCostInfo={drugCostInfo}
        />
      )}
      {hasPrescriptions && (
        <EstimatedMonthlyCosts plan={plan} drugCostInfo={drugCostInfo} />
      )}
      {/* Estimated Total Monthly Drug Cost - Must have prescriptions and pharmacies */}
      {hasPrescriptions && hasPharmacies && (
        <CostsByCoveragePhaseTable drugCostInfo={drugCostInfo} plan={plan} />
      )}

      {hasPrescriptions ? (
        <ToggleMoreBenefits
          buttonTextToOpen={t("plan_terms.drug_coverage.open")}
          buttonTextToCollapse={t("plan_terms.drug_coverage.collapse")}
          section="collapse-more-drug-coverage"
        >
          {/* Costs By Drug Tier - Always shown */}
          {plan.abstract_benefits && (
            <CostsByDrugTierTable abstractBenefits={plan.abstract_benefits} />
          )}
          {/* Other Drug Information - Must have prescriptions */}
          <OtherDrugInformationTable
            {...{
              drugCostInfo,
              drugInfo,
              prescriptions,
            }}
          />

          {/* My Drug List - Must have prescriptions */}
          <MyDrugListTable prescriptions={prescriptions} />

          {/* Part B Drugs - Always shown */}
          <PartBDrugsTable plan={plan} />
        </ToggleMoreBenefits>
      ) : (
        <>
          {/* Costs By Drug Tier - Always shown */}
          {plan.abstract_benefits && (
            <CostsByDrugTierTable abstractBenefits={plan.abstract_benefits} />
          )}
          {/* Part B Drugs - Always shown */}
          <PartBDrugsTable plan={plan} />
        </>
      )}
    </PlanDetailSection>
  );
};
